import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/admin/AdminLayout";
import KPICard from "@/components/admin/KPICard";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { 
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue
} from "@/components/ui/BrutalSelect";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { Loader2, Search, Eye, EyeOff, RefreshCw, Trash2, ArrowLeft, Calendar, Star, Flag } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ContentReportModal } from "@/components/admin/ContentReportModal";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Event {
  id: string;
  title: string;
  slug: string;
  status: string;
  event_type: string;
  event_format: string;
  start_datetime: string;
  location_city: string;
  current_participants: number;
  max_participants: number;
  is_featured: boolean;
  created_at: string;
  organizer?: {
    business_name: string;
    professional_email: string;
    user_id: string;
  };
}

export default function AdminEvents() {
  const { isAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [recentFilter, setRecentFilter] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [stats, setStats] = useState({
    totalEvents: 0,
    publishedEvents: 0,
    totalParticipants: 0,
    featuredEvents: 0,
  });

  useEffect(() => {
    if (!authLoading && !isAdmin()) {
      navigate("/");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    loadEvents();
    loadReportCounts();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [searchQuery, statusFilter, events, recentFilter, reportCounts]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const { data: eventsData, error: eventsError } = await supabase
        .from("events")
        .select("*")
        .order("created_at", { ascending: false });

      if (eventsError) throw eventsError;

      // Get unique organizer IDs
      const organizerIds = [...new Set(eventsData?.map(e => e.organizer_id).filter(Boolean))];
      
      // Fetch organizer profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, business_name, professional_email")
        .in("user_id", organizerIds);

      if (profilesError) throw profilesError;

      // Map profiles to events
      const profilesMap = new Map(profilesData?.map(p => [p.user_id, p]) || []);
      const eventsWithOrganizers = eventsData?.map(event => ({
        ...event,
        organizer: profilesMap.get(event.organizer_id)
      })) || [];

      setEvents(eventsWithOrganizers);
      calculateStats(eventsWithOrganizers);
    } catch (error) {
      console.error("Error loading events:", error);
      toast({
        title: t("admin.events.messages.error"),
        description: t("admin.events.messages.loadError"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: Event[]) => {
    const published = data.filter((e) => e.status === "published").length;
    const participants = data.reduce((sum, e) => sum + (e.current_participants || 0), 0);
    const featured = data.filter((e) => e.is_featured).length;

    setStats({
      totalEvents: data.length,
      publishedEvents: published,
      totalParticipants: participants,
      featuredEvents: featured,
    });
  };

  const loadReportCounts = async () => {
    try {
      const { data } = await supabase
        .from("user_reports")
        .select("reported_event_id")
        .eq("status", "pending")
        .not("reported_event_id", "is", null);

      const counts: Record<string, number> = {};
      data?.forEach((r: any) => {
        counts[r.reported_event_id] = (counts[r.reported_event_id] || 0) + 1;
      });
      setReportCounts(counts);
    } catch (error) {
      console.error("Error loading report counts:", error);
    }
  };
  const filterEvents = () => {
    let filtered = events;

    if (statusFilter === "flagged") {
      filtered = filtered.filter((e) => (reportCounts[e.id] ?? 0) > 0);
    } else if (statusFilter !== "all") {
      filtered = filtered.filter((e) => e.status === statusFilter);
    }

    if (recentFilter) {
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      filtered = filtered.filter((e) => new Date(e.created_at) > oneDayAgo);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (e) =>
          e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          e.location_city?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredEvents(filtered);
  };

  const toggleFeatured = async (eventId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("events")
        .update({ is_featured: !currentStatus })
        .eq("id", eventId);

      if (error) throw error;

      toast({
        title: t("admin.events.messages.success"),
        description: !currentStatus ? t("admin.events.messages.featuredAdded") : t("admin.events.messages.featuredRemoved"),
      });

      loadEvents();
    } catch (error) {
      console.error("Error toggling featured:", error);
      toast({
        title: t("admin.events.messages.error"),
        description: t("admin.events.messages.updateError"),
        variant: "error",
      });
    }
  };

  const toggleStatus = async (eventId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'published' ? 'cancelled' : 'published';
      
      const { error } = await supabase
        .from("events")
        .update({ status: newStatus })
        .eq("id", eventId);

      if (error) throw error;

      toast({
        title: t("admin.events.messages.success"),
        description: newStatus === 'published' ? t("admin.events.messages.eventRepublished") : t("admin.events.messages.eventHidden"),
      });

      loadEvents();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast({
        title: t("admin.events.messages.error"),
        description: t("admin.events.messages.statusUpdateError"),
        variant: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;

    try {
      const { error } = await supabase.from("events").delete().eq("id", eventToDelete);

      if (error) throw error;

      toast({
        title: t("admin.events.messages.success"),
        description: t("admin.events.messages.eventDeleted"),
      });

      loadEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast({
        title: t("admin.events.messages.error"),
        description: t("admin.events.messages.deleteError"),
        variant: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">{t("common.dashboard")}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.events.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.events.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
            <Calendar className="w-10 h-10 text-orange-600" />
            {t("admin.events.title")}
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <KPICard title={t("admin.events.kpi.totalEvents")} value={stats.totalEvents} />
          <KPICard title={t("admin.events.kpi.published")} value={stats.publishedEvents} />
          <KPICard title={t("admin.events.kpi.totalParticipants")} value={stats.totalParticipants} />
          <KPICard title={t("admin.events.kpi.featured")} value={stats.featuredEvents} />
        </div>

        <BrutalCard>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <BrutalInput
                  placeholder={t("admin.events.filters.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <BrutalSelect value={statusFilter} onValueChange={setStatusFilter}>
                <BrutalSelectTrigger className="w-[200px]">
                  <BrutalSelectValue placeholder={t("admin.events.filters.statusPlaceholder")} />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  <BrutalSelectItem value="all">{t("admin.events.filters.allStatuses")}</BrutalSelectItem>
                  <BrutalSelectItem value="published">{t("admin.events.filters.published")}</BrutalSelectItem>
                  <BrutalSelectItem value="draft">{t("admin.events.filters.draft")}</BrutalSelectItem>
                  <BrutalSelectItem value="cancelled">{t("admin.events.filters.cancelled")}</BrutalSelectItem>
                  <BrutalSelectItem value="flagged">{t("admin.events.filters.flagged")}</BrutalSelectItem>
                </BrutalSelectContent>
              </BrutalSelect>
              <label className="flex items-center gap-2 border-[3px] border-dark px-4 py-2 bg-card cursor-pointer">
                <input
                  type="checkbox"
                  checked={recentFilter}
                  onChange={(e) => setRecentFilter(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="font-heading font-bold text-sm">{t("admin.events.filters.recentOnly")}</span>
              </label>
            </div>

            <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b-[3px] border-dark">
                <tr>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.events.table.title")}</th>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.events.table.organizer")}</th>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.events.table.type")}</th>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.events.table.date")}</th>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.events.table.city")}</th>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.events.table.participants")}</th>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.events.table.status")}</th>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.events.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                      {t("admin.events.messages.noEventsFound")}
                    </td>
                  </tr>
                ) : (
                  filteredEvents.map((event) => {
                    const isNew = (new Date().getTime() - new Date(event.created_at).getTime()) < 86400000;
                    return (
                      <tr key={event.id} className="border-b-[3px] border-dark hover:bg-surface">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {isNew && <BrutalBadge variant="warning">{t("admin.events.badges.new")}</BrutalBadge>}
                            {reportCounts[event.id] > 0 && (
                              <BrutalBadge variant="secondary" className="flex items-center gap-1">
                                <Flag className="w-3 h-3" /> {reportCounts[event.id]}
                              </BrutalBadge>
                            )}
                            <span className="font-bold">{event.title}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <p className="font-bold">{event.organizer?.business_name || t("admin.events.fallback.notAvailable")}</p>
                            <p className="text-xs text-muted-foreground">{event.organizer?.professional_email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 uppercase text-xs">{event.event_type}</td>
                        <td className="px-6 py-4">
                          {format(new Date(event.start_datetime), "dd/MM/yyyy HH:mm", { locale: it })}
                        </td>
                        <td className="px-6 py-4">{event.location_city || t("admin.events.fallback.noCity")}</td>
                        <td className="px-6 py-4">
                          {event.current_participants || 0}
                          {event.max_participants ? ` / ${event.max_participants}` : ""}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-1 text-xs font-bold uppercase ${
                              event.status === "published"
                                ? "bg-green-100 text-green-800"
                                : event.status === "draft"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {event.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {reportCounts[event.id] > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => { setSelectedEvent(event); setReportModalOpen(true); }}
                                      className="p-2 border-[3px] border-dark bg-destructive text-destructive-foreground shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                      aria-label="Vedi segnalazioni"
                                    >
                                      <Flag className="w-4 h-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Vedi segnalazioni ({reportCounts[event.id]})</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => window.open(`/event/${event.slug}`, '_blank')}
                                    className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                    aria-label={t("admin.events.buttons.viewDetails")}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t("admin.events.buttons.viewDetails")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => toggleStatus(event.id, event.status)}
                                    className={`p-2 border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all ${
                                      event.status === 'published' ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-dark'
                                    }`}
                                    aria-label={event.status === 'published' ? t("admin.events.buttons.hideEvent") : t("admin.events.buttons.republishEvent")}
                                  >
                                    {event.status === 'published' ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{event.status === 'published' ? t("admin.events.buttons.hideEvent") : t("admin.events.buttons.republishEvent")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => toggleFeatured(event.id, event.is_featured)}
                                    className={`p-2 border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all ${
                                      event.is_featured ? 'bg-warning text-dark' : 'bg-card'
                                    }`}
                                    aria-label={event.is_featured ? t("admin.events.buttons.removeFromFeatured") : t("admin.events.buttons.setFeatured")}
                                  >
                                    <Star className={`w-4 h-4 ${event.is_featured ? 'fill-current' : ''}`} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{event.is_featured ? t("admin.events.buttons.removeFromFeatured") : t("admin.events.buttons.setFeatured")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => {
                                      setEventToDelete(event.id);
                                      setDeleteDialogOpen(true);
                                    }}
                                    className="p-2 border-[3px] border-dark bg-destructive text-destructive-foreground shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                    aria-label={t("admin.events.buttons.deleteEvent")}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t("admin.events.buttons.deleteEvent")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          </div>
        </BrutalCard>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.events.dialog.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.events.dialog.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("admin.events.dialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t("admin.events.dialog.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedEvent && (
        <ContentReportModal
          isOpen={reportModalOpen}
          onClose={() => {
            setReportModalOpen(false);
            setSelectedEvent(null);
          }}
          contentType="event"
          contentId={selectedEvent.id}
          contentTitle={selectedEvent.title}
          onAction={() => {
            loadEvents();
            loadReportCounts();
          }}
        />
      )}
    </AdminLayout>
  );
}
